/* global console, Word */
import {
  deleteRangeCC,
  deleteRangeSelection,
  getXmlByCCId,
  getXMLFromSelection,
  insertXmlIntoSelection,
  toggleDeleteAndEditCC,
} from "./WordXMLManager";
import {
  DocPropKeys,
  getAllCoObjects,
  getCustomXmlByNamespaceId,
  retrieveDocumentProp,
} from "../utils/helpers/CustomXmlHelper";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../utils/constants/eventChannel";
import { inheritComponentData } from "../utils/helpers/ComponentInstanceHelper";
import { insertComponentInstance, insertElaborationCCByType } from "../redux/feature/cpm/cpm-slice";
import { store } from "../redux/store/store";
import { selectCpmUsername } from "../redux/feature/settings/configurations-slice";
import { CoObjectItem } from "../components/interfaces/CoObject";
import { checkIfSelected, getParentContentControls, selectContentControl } from "./ContentControl";
import { setPanelMessage } from "../redux/feature/panelMessage/panelMessage-slice";
import ContentControl = Word.ContentControl;
import { CoObjectTypeEnum } from "../api/models";

/*
 *  Observation
 *  3) gestire caso in cui cc ha cc inside (vedi free text)
 *
 * */
export interface DeletableAndEditableCC {
  id: Word.ContentControl["id"];
  cannotDelete: boolean;
  cannotEdit: boolean;
}

interface PastedCCResult {
  title: string;
  id: number;
}

class CopyCutPaste {
  private instance: CopyCutPaste;
  private action: "CUT" | "COPY";
  private CCsSelected: DeletableAndEditableCC[] = [];

  private ccsXmlString: string = "";

  public getInstance() {
    if (!this.instance) this.instance = new CopyCutPaste();
    return this.instance;
  }

  async cut() {
    const ccs = await this.getCCSelected();
    if (!ccs) return false;

    const CCsList = await this.getList(ccs);
    if (!CCsList) return false;
    this.action = "CUT";
    this.CCsSelected = CCsList;
    await this.toggleCCStatus(this.CCsSelected.map((cc) => ({ id: cc.id, cannotEdit: false, cannotDelete: false })));
    const isCCArray = Array.isArray(ccs);
    if (isCCArray) {
      await this.cutMultipleCC();
      return true;
    }
    const coObjectItem = await getCustomXmlByNamespaceId(ccs.id.toString());
    if (coObjectItem && coObjectItem.info.objectType == CoObjectTypeEnum.FREE_TEXT) {
      await selectContentControl(coObjectItem);
      return this.cut();
    }
    await this.cutSingleCC(ccs.id);
    return true;
  }

  async copy() {
    const ccs = await this.getCCSelected();
    if (!ccs) return false;

    const CCsList = await this.getList(ccs);
    if (!CCsList) return false;
    this.action = "COPY";
    this.CCsSelected = CCsList;
    const isCCArray = Array.isArray(ccs);
    if (isCCArray) {
      await this.copyMultipleCC();
      store.dispatch(setPanelMessage("COPIED_SUCCESSFULLY"));
      return true;
    }
    const coObjectItem = await getCustomXmlByNamespaceId(ccs.id.toString());
    if (coObjectItem && coObjectItem.info.objectType == CoObjectTypeEnum.FREE_TEXT) {
      await selectContentControl(coObjectItem);
      return this.copy();
    }
    await this.copySingleCC(ccs.id);
    store.dispatch(setPanelMessage("COPIED_SUCCESSFULLY"));
    return true;
  }

  async paste(): Promise<[boolean, null | CoObjectItem[]]> {
    const cc = await getParentContentControls();
    if (cc) {
      const coObjectItem = await getCustomXmlByNamespaceId(cc.id.toString());
      const isFreetext = coObjectItem.info.objectType == CoObjectTypeEnum.FREE_TEXT;
      if (!isFreetext) {
        store.dispatch(setPanelMessage("CANNOT_PASTE_INSIDE_CC"));
        console.error("YOU CANNOT PASTE INSIDE A CC");
        return [false, null];
      }
    }

    if (this.action === "CUT") {
      const pastedCcs = await this.pasteXMLString();
      await this.restoreCCStatus(pastedCcs);
      this.reset();
      return [true, null];
    }
    if (this.action === "COPY") {
      const pastedElements = await this.pasteFromCopy();
      this.reset();
      return [true, pastedElements];
    }
  }

  public getCCsSelected() {
    return this.CCsSelected;
  }

  //#region ---- CC UTILITIES ----
  async getList(ccs: ContentControl | ContentControl[]) {
    const isCCArray = Array.isArray(ccs);
    const CCsList = isCCArray ? ccs : [ccs];
    const areDocumentPartSelected = await this.hasDocumentParts(CCsList.map((cc) => cc.id));
    if (areDocumentPartSelected) {
      store.dispatch(setPanelMessage("CANNOT_PERFORM_ACTION_ON_DOCUMENT_PART_INCLUDE"));
      return false;
    }
    return CCsList;
  }

  private async getCCSelected(): Promise<ContentControl | ContentControl[] | null> {
    try {
      const selection = await checkIfSelected();
      if (selection) return selection;
      store.dispatch(setPanelMessage("NO_CC_SELECTED"));
      return null;
    } catch (err) {
      console.error(err);
    }
  }

  private async restoreCCStatus(ccs: PastedCCResult[]) {
    const newCCs = ccs.reduce<DeletableAndEditableCC[]>((res, cc) => {
      const originalCC = this.CCsSelected.find((o) => cc.id === o.id);
      res.push({ ...cc, cannotEdit: originalCC.cannotEdit, cannotDelete: originalCC.cannotDelete });
      return res;
    }, []);

    await this.toggleCCStatus(newCCs); // Restore cannotEdit and cannotDelete
  }

  private async toggleCCStatus(ccs: DeletableAndEditableCC[]) {
    const toggleStatusCC = ccs.map(toggleDeleteAndEditCC);
    await Promise.all(toggleStatusCC);
  }

  private async hasDocumentParts(ccIds: number[]) {
    const documentId = (await retrieveDocumentProp(DocPropKeys.DocumentId)) ?? EVENT_CHANNEL_DEFAULT_TOPIC;
    const ccList = await getAllCoObjects(documentId);
    const filteredCCListIds = ccList.reduce((acc, cc) => {
      if (cc.info.objectType === "DOCUMENT_PART_INCLUDE") return acc;
      return [...acc, cc.id];
    }, []);
    for (const id of ccIds) {
      const ccObject = await this.getCCObj(id + "");
      if (!ccObject) {
        const isIncludedInADocPart = !filteredCCListIds.includes(id);
        if (isIncludedInADocPart) return true;
      }
      const isDocumentPart = ccObject?.info?.objectType === "DOCUMENT_PART_INCLUDE";
      if (isDocumentPart) return true;
    }
    return false;
  }

  private createNewCCObj = (coObject: CoObjectItem) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...otherData } = coObject;
    const copiedName = otherData.info.objectName;
    const newName = `${copiedName}_Copy`;

    this.ccsXmlString = this.ccsXmlString.replace(copiedName, newName);

    return {
      ...otherData,
      contentControlId: "",
      info: { ...otherData.info, objectName: newName },
    };
  };

  //endregion

  //#region ---- GETTING UTILITIES ----
  private async getCCObj(copiedCCId: string) {
    const coObjectItem = await getCustomXmlByNamespaceId(copiedCCId + "");

    if (coObjectItem?.info?.componentReference) {
      return await inheritComponentData(coObjectItem);
    }
    return coObjectItem;
  }

  //endregion

  //#region ---- PASTE UTILITIES ----

  private async pasteXMLString(): Promise<PastedCCResult[]> {
    return await Word.run(async (ctx) => {
      try {
        const range = await insertXmlIntoSelection(ctx, this.ccsXmlString);
        range.load(["items"]);
        await ctx.sync();
        const ccInRange = range.getContentControls();
        ccInRange.load(["items", "title", "id"]);
        await ctx.sync();

        return ccInRange.items.map((cc) => ({ title: cc.title, id: cc.id }));
      } catch (err) {
        console.error("ERROR LOOKING FOR CCs AFTER PASTE", err);
        return [];
      }
    });
  }

  private async pasteFromCopy() {
    const documentId = (await retrieveDocumentProp(DocPropKeys.DocumentId)) ?? EVENT_CHANNEL_DEFAULT_TOPIC;
    const username = selectCpmUsername(store.getState());

    const getCoObjPromises: Promise<CoObjectItem>[] = this.CCsSelected.map(({ id: copiedCCId }) => {
      return this.getCCObj(copiedCCId + "").then(this.createNewCCObj);
    });

    const CoObjectsList = await Promise.all(getCoObjPromises).catch((e) => {
      console.error("ALL CATCH", e);
      return [];
    });

    const pastedCC = await this.pasteXMLString();

    const insertDBPromises: Promise<CoObjectItem | null>[] = pastedCC.map((el) => {
      const cc = CoObjectsList.find((c) => c.info.objectName === el.title);
      if (!cc) {
        deleteRangeCC(el.id);
        console.error("New CC NOT FOUND IN THE DOCUMENT");
        return Promise.resolve(null);
      }

      cc.contentControlId = el.id + "";
      cc.documentId = documentId;
      if (cc.info.componentReference !== undefined) return insertComponentInstance(cc, username);
      else return insertElaborationCCByType({ coObject: cc, userId: username });
    });

    return await Promise.all(insertDBPromises);
  }

  //endregion

  //#region ---- COPY UTILITIES ----

  private async copySingleCC(ccId: number) {
    const xmlString = await getXmlByCCId(ccId);
    this.setccsXmlString(xmlString);
  }

  private async copyMultipleCC() {
    const xmlString = await getXMLFromSelection();
    this.setccsXmlString(xmlString);
  }

  //endregion

  //#region ---- CUT UTILITIES ----

  private async cutMultipleCC() {
    await this.copyMultipleCC();
    await deleteRangeSelection();
  }

  private async cutSingleCC(ccId: number) {
    await this.copySingleCC(ccId);
    await deleteRangeCC(ccId);
  }

  //endregion

  //#region ---- CLASS UTILITIES ----

  private setccsXmlString(xmlString: string) {
    if (!xmlString) return "";
    this.ccsXmlString = xmlString;
  }

  private reset() {
    this.action = null;
    this.CCsSelected = [];
    this.ccsXmlString = "";
  }

  //endregion
}

export const CopyCutPasteInstance = new CopyCutPaste().getInstance();
